import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { format, subDays } from 'date-fns'
import { es } from 'date-fns/locale'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { LuList, LuSearch, LuArrowUp, LuArrowDown, LuChevronLeft, LuChevronRight, LuDownload, LuSheet } from 'react-icons/lu'
import api from '../services/api'
import './MovementsxPage.css'

// Registrar locale español para el datepicker
registerLocale('es', es)

const MW_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

function normalizeMwTime(value) {
  const text = String(value || '').trim()
  return MW_TIME_RE.test(text) ? text : ''
}

/** Mismo contrato que el middleware: sin hora el día es completo; con hora el recorte es inclusivo. */
function buildMwDateRange({ fromDate, toDate, fromTime = '', toTime = '' } = {}) {
  const fromH = normalizeMwTime(fromTime)
  const toH = normalizeMwTime(toTime)
  const useTime = Boolean(fromH || toH)
  const fromParam = fromDate
    ? (useTime ? `${format(fromDate, 'yyyy-MM-dd')} ${fromH || '00:00'}:00` : format(fromDate, 'yyyy-MM-dd'))
    : ''
  const toParam = toDate
    ? (useTime ? `${format(toDate, 'yyyy-MM-dd')} ${toH ? `${toH}:00` : '23:59:59'}` : format(toDate, 'yyyy-MM-dd'))
    : ''
  const fromBound = fromParam
    ? new Date(useTime ? fromParam.replace(' ', 'T') : `${fromParam}T00:00:00`)
    : null
  const toBound = toParam
    ? new Date(useTime ? toParam.replace(' ', 'T') : `${toParam}T23:59:59`)
    : null
  return {
    rangePrecision: useTime ? 'datetime' : 'day',
    fromParam,
    toParam,
    fromBound,
    toBound
  }
}

function movementInDateTimeRange(mov, from, to, { requireDatetime = false } = {}) {
  const raw = mov?.transaction_data?.datetime
  if (!raw) return !requireDatetime
  const at = new Date(String(raw).replace(' ', 'T'))
  if (Number.isNaN(at.getTime())) return !requireDatetime
  if (from && at < from) return false
  if (to && at > to) return false
  return true
}

// Función para formatear monto con puntos de miles (formato argentino)
const formatAmount = (value) => {
  const num = parseFloat(value) || 0
  return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Función para formatear fecha de notificación
const formatNotificationDate = (dateString) => {
  if (!dateString) return '-'
  try {
    // El datetime viene en formato "YYYY-MM-DD HH:mm:ss"
    const date = new Date(dateString.replace(' ', 'T'))
    return format(date, 'dd/MM/yyyy HH:mm:ss', { locale: es })
  } catch (e) {
    return dateString
  }
}

function MovementsxPage() {
  const { assignedAccounts, username } = useAuth()
  
  // Detectar si es el operador urbana_4315
  const isUrbana = username === 'urbana_4315'
  
  // Estados
  const [selectedAccounts, setSelectedAccounts] = useState([]) // Array de account_numbers seleccionados
  const [manualCvu, setManualCvu] = useState('') // CVU manual para urbana_4315
  const [notifications, setNotifications] = useState([])
  const [movementsWithCommissions, setMovementsWithCommissions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 }) // Progreso de carga
  const [error, setError] = useState('')
  const [searchPerformed, setSearchPerformed] = useState(false)
  
  // Filtros de fecha
  const [fromDate, setFromDate] = useState(subDays(new Date(), 7))
  const [toDate, setToDate] = useState(new Date())
  const [filterByHour, setFilterByHour] = useState(false)
  const [fromTime, setFromTime] = useState('')
  const [toTime, setToTime] = useState('')

  const handleFilterByHourChange = (on) => {
    setFilterByHour(on)
    if (on) {
      setFromTime((prev) => prev || '00:00')
      setToTime((prev) => prev || '23:59')
      return
    }
    setFromTime('')
    setToTime('')
  }
  
  // Paginación del API (para cargar datos)
  const [page, setPage] = useState(1)
  const [totalNotifications, setTotalNotifications] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [tooManyResults, setTooManyResults] = useState(false)
  
  // Paginación de visualización (100 por página)
  const [displayPage, setDisplayPage] = useState(1)
  
  // Filtros de búsqueda
  const [filterAmount, setFilterAmount] = useState('')
  const [filterDescription, setFilterDescription] = useState('')
  const [filterCoelsaId, setFilterCoelsaId] = useState('')
  const [filterDocumento, setFilterDocumento] = useState('')
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  
  // Resetear página de visualización cuando cambian los filtros
  useEffect(() => {
    setDisplayPage(1)
  }, [filterAmount, filterDescription, filterCoelsaId, filterDocumento])

  // Ref a la tabla para scroll-to-top al cambiar de página
  const tableRef = useRef(null)

  // Al cambiar de página, scrolleamos al tope de la tabla para que se vea
  // claramente que el contenido se actualizó.
  useEffect(() => {
    if (!tableRef.current) return
    tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [displayPage])
  
  // Tope server-side del endpoint /notifications del middleware
  const PAGE_API_SIZE = 2000
  // Tope total por CVU (safety net): 50k = 25 páginas de 2k.
  // Si un CVU tiene más, se muestra warning y se cortan los más viejos.
  const MAX_RECORDS_PER_CVU = 50000
  const PAGE_SIZE = 100 // Movimientos por página para visualización

  // Obtener los CVUs de las cuentas seleccionadas (o el CVU manual para urbana)
  const getSelectedCVUs = () => {
    if (isUrbana) {
      return [{ accountNumber: 'manual', cvu: manualCvu.trim() }]
    }
    return selectedAccounts.map(accNum => {
      const account = assignedAccounts?.find(acc => acc.account_number === accNum)
      return { accountNumber: accNum, cvu: account?.cvu || '' }
    }).filter(item => item.cvu)
  }

  // Obtener el nombre/número de la cuenta seleccionada (para mostrar en comisiones)
  const getSelectedAccountName = (accountNumber) => {
    return accountNumber || 'Cuenta'
  }
  
  // Límite máximo de cuentas seleccionables
  const MAX_ACCOUNTS = 5
  
  // Estado para mostrar mensaje de límite alcanzado
  const [showLimitMessage, setShowLimitMessage] = useState(false)
  
  // Toggle selección de una cuenta
  const toggleAccountSelection = (accountNumber) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountNumber)) {
        // Deseleccionar - siempre permitido
        setShowLimitMessage(false)
        return prev.filter(acc => acc !== accountNumber)
      } else {
        // Seleccionar - verificar límite
        if (prev.length >= MAX_ACCOUNTS) {
          setShowLimitMessage(true)
          setTimeout(() => setShowLimitMessage(false), 3000) // Ocultar después de 3 segundos
          return prev // No agregar
        }
        setShowLimitMessage(false)
        return [...prev, accountNumber]
      }
    })
  }
  
  // Toggle todas las cuentas (hasta el máximo)
  const toggleAllAccounts = () => {
    if (!assignedAccounts) return
    
    if (selectedAccounts.length > 0) {
      // Deseleccionar todas
      setSelectedAccounts([])
      setShowLimitMessage(false)
    } else {
      // Seleccionar hasta el máximo
      const accountsToSelect = assignedAccounts.slice(0, MAX_ACCOUNTS).map(acc => acc.account_number)
      setSelectedAccounts(accountsToSelect)
      if (assignedAccounts.length > MAX_ACCOUNTS) {
        setShowLimitMessage(true)
        setTimeout(() => setShowLimitMessage(false), 3000)
      }
    }
  }
  
  // Verificar si todas las cuentas están seleccionadas (o el máximo)
  const allAccountsSelected = selectedAccounts.length >= Math.min(assignedAccounts?.length || 0, MAX_ACCOUNTS)

  // Función para buscar notificaciones
  const handleSearch = async () => {
    // Validación diferente para urbana (usa CVU manual) vs otros usuarios (usa select)
    if (isUrbana) {
      if (!manualCvu.trim()) {
        setError('Debe ingresar un CVU')
        return
      }
    } else {
      if (selectedAccounts.length === 0) {
        setError('Debe seleccionar al menos una cuenta')
        return
      }
    }

    const cvuList = getSelectedCVUs()
    if (cvuList.length === 0) {
      setError('No se encontraron CVUs de las cuentas seleccionadas')
      return
    }

    const mwRange = buildMwDateRange({
      fromDate,
      toDate,
      fromTime: filterByHour ? fromTime : '',
      toTime: filterByHour ? toTime : ''
    })
    if (mwRange.fromBound && mwRange.toBound && mwRange.fromBound.getTime() > mwRange.toBound.getTime()) {
      setError('La fecha "Desde" no puede ser posterior a "Hasta"')
      return
    }

    setLoading(true)
    setError('')
    setSearchPerformed(true)
    setPage(1)
    setDisplayPage(1)
    setTooManyResults(false)
    setLoadingProgress({ current: 0, total: cvuList.length })

    try {
      let allNotifications = []
      let totalCount = 0
      let hasTooManyResults = false

      const fromCutoff = mwRange.fromBound

      // Consultar cada cuenta secuencialmente para mostrar progreso
      for (let i = 0; i < cvuList.length; i++) {
        const { accountNumber, cvu } = cvuList[i]
        setLoadingProgress({ current: i + 1, total: cvuList.length })

        let offset = 0
        let collected = 0
        let keepPaging = true
        const cvuNotifications = []

        while (keepPaging) {
          const response = await api.get('/notifications', {
            params: {
              cvu: cvu,
              limit: PAGE_API_SIZE,
              offset,
              ...(mwRange.fromParam ? { from: mwRange.fromParam } : {}),
              ...(mwRange.toParam ? { to: mwRange.toParam } : {})
            }
          })

          const data = response.data
          const pageNotifs = Array.isArray(data?.notifications) ? data.notifications : []
          if (pageNotifs.length === 0) break

          cvuNotifications.push(...pageNotifs)
          collected += pageNotifs.length
          offset += PAGE_API_SIZE

          // Si la página fue parcial, no hay más datos.
          if (pageNotifs.length < PAGE_API_SIZE) {
            keepPaging = false
            break
          }

          // Safety cap: ya bajamos demasiados registros de este CVU
          if (collected >= MAX_RECORDS_PER_CVU) {
            hasTooManyResults = true
            keepPaging = false
            break
          }

          // Si el más viejo de la página ya quedó antes del fromDate, no tiene
          // sentido seguir (el endpoint pagina por orden DESC).
          if (fromCutoff) {
            const last = pageNotifs[pageNotifs.length - 1]
            const lastDtStr = last?.transaction_data?.datetime || last?.received_at
            if (lastDtStr) {
              const lastDt = new Date(String(lastDtStr).replace(' ', 'T'))
              if (!Number.isNaN(lastDt.getTime()) && lastDt < fromCutoff) {
                keepPaging = false
                break
              }
            }
          }
        }

        // Agregar el número de cuenta a cada notificación
        const notificationsWithAccount = cvuNotifications.map(notif => ({
          ...notif,
          _accountNumber: accountNumber
        }))

        allNotifications = [...allNotifications, ...notificationsWithAccount]
        totalCount += cvuNotifications.length
      }
      
      // Ordenar todas las notificaciones por fecha descendente
      allNotifications.sort((a, b) => {
        const dateA = new Date(a.transaction_data?.datetime?.replace(' ', 'T') || a.received_at)
        const dateB = new Date(b.transaction_data?.datetime?.replace(' ', 'T') || b.received_at)
        return dateB.getTime() - dateA.getTime()
      })
      
      const filteredNotifications = allNotifications.filter((notif) => (
        movementInDateTimeRange(notif, mwRange.fromBound, mwRange.toBound, {
          requireDatetime: mwRange.rangePrecision === 'datetime'
        })
      ))
      
      setNotifications(filteredNotifications)
      setTotalNotifications(totalCount)
      setHasMore(hasTooManyResults)
      setTooManyResults(hasTooManyResults)
      
      setMovementsWithCommissions(filteredNotifications)
      
    } catch (err) {
      console.error('Error fetching notifications:', err)
      setError('Error al consultar las notificaciones: ' + (err.response?.data?.message || err.message))
      setNotifications([])
    } finally {
      setLoading(false)
      setLoadingProgress({ current: 0, total: 0 })
    }
  }
  
  // Limpiar filtros
  const handleClearFilters = () => {
    setFilterAmount('')
    setFilterDescription('')
    setFilterCoelsaId('')
    setFilterDocumento('')
  }

  // Función para limpiar el filtro de importe (formato argentino)
  const cleanFilterAmount = (value) => {
    if (!value) return { number: 0, isPositive: false, isNegative: false }
    
    const isPositive = value.startsWith('+')
    const isNegative = value.startsWith('-')
    
    // Remover signos y espacios
    let cleaned = value.replace(/[+\-\s]/g, '')
    // Formato argentino: puntos son miles, coma es decimal
    cleaned = cleaned.replace(/\./g, '').replace(',', '.')
    
    return { 
      number: parseFloat(cleaned) || 0, 
      isPositive, 
      isNegative 
    }
  }

  // Determinar tipo de movimiento (Entrante/Saliente)
  const getTransferType = (notification) => {
    const type = notification.transaction_data?.transfer_type
    if (type === 'E') return { label: 'Entrante', icon: <LuArrowDown className="icon-incoming" />, className: 'incoming' }
    if (type === 'S') return { label: 'Saliente', icon: <LuArrowUp className="icon-outgoing" />, className: 'outgoing' }
    return { label: '-', icon: null, className: '' }
  }

  // Formatear monto con signo según tipo
  const formatAmountWithSign = (notification) => {
    const amount = parseFloat(notification.transaction_data?.amount || notification.amount || 0)
    const type = notification.transaction_data?.transfer_type
    
    if (type === 'S') {
      return `-$${formatAmount(amount)}`
    }
    return `$${formatAmount(amount)}`
  }

  // Descargar comprobante de un movimiento
  const handleDownloadComprobante = (mov) => {
    if (!mov) return

    const txData = mov.transaction_data || {}
    const amount = parseFloat(txData.amount || 0)
    const isCredit = txData.transfer_type === 'E'

    let tipoMovimiento = ''
    let directionGlyph = ''
    if (isCredit) {
      tipoMovimiento = 'Transferencia recibida'
      directionGlyph = '↓'
    } else {
      tipoMovimiento = 'Transferencia enviada'
      directionGlyph = '↑'
    }

    // Nº de documento (usa COELSA o AUTH; fallback timestamp)
    const docNumber =
      (txData.coelsa_id && txData.coelsa_id !== '-' && txData.coelsa_id) ||
      (txData.authorization_code && !txData.authorization_code.startsWith('COM-') && txData.authorization_code) ||
      `Z-${Date.now().toString(36).toUpperCase()}`

    const generatedAt = new Date().toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    })

    const dateString = formatNotificationDate(txData.datetime)
    const signo = isCredit && !isCommission ? '' : '-'

    const fileTitle = `comprobante-${docNumber}`.replace(/[^a-zA-Z0-9-]/g, '_')

    const safe = (v) => (v == null ? '' : String(v))

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>${fileTitle}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
          *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

          :root {
            --ink: #0B0B0F;
            --muted: #6B7280;
            --line: #E5E7EB;
            --line-strong: #111827;
            --paper: #FFFFFF;
            --accent: #1D4ED8;
            --accent-dark: #D05A15;
          }

          html, body {
            background: #F4F5F7;
            color: var(--ink);
            font-family: 'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, sans-serif;
            -webkit-font-smoothing: antialiased;
            font-feature-settings: "ss01", "cv11";
          }

          body {
            display: flex;
            justify-content: center;
            padding: 32px 16px 80px;
          }

          .sheet {
            width: 100%;
            max-width: 640px;
            background: var(--paper);
            padding: 56px 56px 40px;
            position: relative;
          }

          /* Franja superior naranja como acento de marca */
          .sheet::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, var(--accent), var(--accent-dark));
          }

          /* ===== Header ===== */
          .header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 32px;
            margin-bottom: 44px;
          }

          .title-block {
            display: flex;
            align-items: center;
            gap: 14px;
          }

          .title-mark {
            width: 10px;
            height: 36px;
            background: var(--accent);
            border-radius: 2px;
            flex-shrink: 0;
          }

          .title-text h1 {
            font-size: 26px;
            font-weight: 700;
            letter-spacing: -0.015em;
            line-height: 1.1;
            color: var(--ink);
          }

          .title-text span {
            display: block;
            margin-top: 4px;
            font-size: 9.5px;
            font-weight: 600;
            letter-spacing: 0.26em;
            text-transform: uppercase;
            color: var(--muted);
          }

          .doc-meta {
            text-align: right;
            font-family: 'JetBrains Mono', ui-monospace, monospace;
            font-size: 11px;
            line-height: 1.7;
            color: var(--ink);
            letter-spacing: 0.04em;
          }

          .doc-meta strong {
            display: block;
            font-weight: 700;
            color: var(--accent-dark);
            font-size: 10px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            margin-bottom: 4px;
            font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
          }

          /* ===== Amount block ===== */
          .amount-block {
            border-top: 1px solid var(--line-strong);
            border-bottom: 1px solid var(--line);
            padding: 28px 0 32px;
            margin-bottom: 36px;
          }

          .amount-label {
            font-size: 10px;
            font-weight: 600;
            letter-spacing: 0.22em;
            text-transform: uppercase;
            color: var(--muted);
            margin-bottom: 14px;
          }

          .amount-value {
            display: flex;
            align-items: baseline;
            gap: 14px;
            font-variant-numeric: tabular-nums;
          }

          .amount-glyph {
            font-size: 28px;
            font-weight: 500;
            color: var(--accent);
            line-height: 1;
          }

          .amount-number {
            font-size: 56px;
            font-weight: 300;
            letter-spacing: -0.02em;
            line-height: 1;
          }

          .amount-currency {
            font-size: 18px;
            font-weight: 500;
            color: var(--muted);
            margin-bottom: 4px;
          }

          .amount-type {
            margin-top: 14px;
            font-size: 13px;
            font-weight: 500;
            color: var(--ink);
            letter-spacing: 0.01em;
          }

          /* ===== Parties (Origen / Destino) ===== */
          .parties {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0 36px;
            padding-bottom: 32px;
            border-bottom: 1px solid var(--line);
            margin-bottom: 28px;
          }

          .party-label {
            font-size: 9.5px;
            font-weight: 700;
            letter-spacing: 0.24em;
            text-transform: uppercase;
            color: var(--muted);
            margin-bottom: 10px;
          }

          .party-name {
            font-size: 15px;
            font-weight: 600;
            color: var(--ink);
            line-height: 1.35;
            margin-bottom: 10px;
            word-break: break-word;
          }

          .party-field {
            margin-top: 6px;
          }

          .party-field-label {
            display: block;
            font-size: 9px;
            font-weight: 500;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: var(--muted);
            margin-bottom: 2px;
          }

          .party-field-value {
            font-family: 'JetBrains Mono', ui-monospace, monospace;
            font-size: 12px;
            color: var(--ink);
            word-break: break-all;
          }

          /* ===== Refs (auth / coelsa) ===== */
          .refs {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0 36px;
            padding-bottom: 28px;
            border-bottom: 1px solid var(--line);
            margin-bottom: 28px;
          }

          .ref-label {
            font-size: 9.5px;
            font-weight: 700;
            letter-spacing: 0.24em;
            text-transform: uppercase;
            color: var(--muted);
            margin-bottom: 6px;
          }

          .ref-value {
            font-family: 'JetBrains Mono', ui-monospace, monospace;
            font-size: 12px;
            font-weight: 500;
            color: var(--ink);
            word-break: break-all;
          }

          /* ===== Footer ===== */
          .footer {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            gap: 24px;
            font-size: 10.5px;
            color: var(--muted);
            line-height: 1.55;
          }

          .footer strong {
            color: var(--ink);
            font-weight: 600;
          }

          .footer-right {
            text-align: right;
            font-family: 'JetBrains Mono', ui-monospace, monospace;
            font-size: 10px;
            letter-spacing: 0.04em;
          }

          /* ===== Print ===== */
          @media print {
            html, body { background: white; }
            body { padding: 0; }
            .sheet {
              max-width: none;
              width: 100%;
              padding: 24mm 22mm;
            }
          }

          @page {
            size: A4;
            margin: 0;
          }

          /* ===== Mobile ===== */
          @media (max-width: 540px) {
            .sheet { padding: 32px 24px; }
            .parties, .refs { grid-template-columns: 1fr; gap: 20px 0; }
            .amount-number { font-size: 44px; }
            .header { flex-direction: column; gap: 18px; }
            .doc-meta { text-align: left; }
            .footer { flex-direction: column; align-items: flex-start; }
            .footer-right { text-align: left; }
          }
        </style>
      </head>
      <body>
        <article class="sheet">
          <header class="header">
            <div class="title-block">
              <div class="title-mark"></div>
              <div class="title-text">
                <h1>Comprobante</h1>
                <span>Constancia de operación</span>
              </div>
            </div>
            <div class="doc-meta">
              <strong>N.º documento</strong>
              ${safe(docNumber)}
            </div>
          </header>

          <section class="amount-block">
            <div class="amount-label">Monto</div>
            <div class="amount-value">
              <span class="amount-glyph">${directionGlyph}</span>
              <span class="amount-currency">$</span>
              <span class="amount-number">${signo}${formatAmount(amount)}</span>
            </div>
            <div class="amount-type">${tipoMovimiento} · ${safe(dateString)}</div>
          </section>

          <section class="parties">
            <div>
              <div class="party-label">Origen</div>
              <div class="party-name">${safe(txData.cardholder_from) || '—'}</div>
              ${txData.cbu_from && txData.cbu_from !== '-' ? `
                <div class="party-field">
                  <span class="party-field-label">CVU</span>
                  <span class="party-field-value">${safe(txData.cbu_from)}</span>
                </div>
              ` : ''}
              ${txData.origin_tax_id ? `
                <div class="party-field">
                  <span class="party-field-label">CUIT / DNI</span>
                  <span class="party-field-value">${safe(txData.origin_tax_id)}</span>
                </div>
              ` : ''}
            </div>
            <div>
              <div class="party-label">Destino</div>
              <div class="party-name">${safe(txData.cardholder_to) || '—'}</div>
              ${txData.cbu_to && txData.cbu_to !== '-' ? `
                <div class="party-field">
                  <span class="party-field-label">CVU</span>
                  <span class="party-field-value">${safe(txData.cbu_to)}</span>
                </div>
              ` : ''}
              ${txData.destination_tax_id ? `
                <div class="party-field">
                  <span class="party-field-label">CUIT / DNI</span>
                  <span class="party-field-value">${safe(txData.destination_tax_id)}</span>
                </div>
              ` : ''}
            </div>
          </section>

          <section class="refs">
            <div>
              <div class="ref-label">Código de autorización</div>
              <div class="ref-value">${(txData.authorization_code && !txData.authorization_code.startsWith('COM-')) ? safe(txData.authorization_code) : '—'}</div>
            </div>
            <div>
              <div class="ref-label">COELSA ID</div>
              <div class="ref-value">${(txData.coelsa_id && txData.coelsa_id !== '-') ? safe(txData.coelsa_id) : '—'}</div>
            </div>
          </section>

          <footer class="footer">
            <div>
              <strong>Constancia digital.</strong><br/>
              Este documento acredita la operación realizada. No requiere firma.
            </div>
            <div class="footer-right">
              Emitido<br/>${generatedAt}
            </div>
          </footer>
        </article>
        <script>
          window.onload = function () {
            document.title = '${fileTitle}';
            setTimeout(function () { window.print(); }, 200);
          };
        </script>
      </body>
      </html>
    `)
    printWindow.document.close()
  }

  // Exportar listado a CSV/Excel
  const handleExport = () => {
    if (notifications.length === 0 && !searchPerformed) return

    const cvuList = getSelectedCVUs()
    if (cvuList.length === 0) {
      setError('Debe seleccionar al menos una cuenta para exportar')
      return
    }

    const fromStr = format(fromDate, 'yyyy-MM-dd')
    const toStr = format(toDate, 'yyyy-MM-dd')

    const params = new URLSearchParams()
    cvuList.forEach(item => params.append('cvus', item.cvu))
    params.append('from', fromStr)
    params.append('to', toStr)

    if (filterAmount) {
      const { number: amountFilter, isPositive, isNegative } = cleanFilterAmount(filterAmount)
      if (amountFilter > 0) {
        params.append('amount', isNegative ? -amountFilter : amountFilter)
      }
    }
    if (filterDescription) {
      params.append('cvu_search', filterDescription.trim())
    }
    if (filterCoelsaId) {
      params.append('coelsa_id', filterCoelsaId.trim())
    }
    if (filterDocumento) {
      params.append('document', filterDocumento.trim())
    }

    const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '')
    const exportUrl = `${apiBase}/notifications/export?${params.toString()}`
    window.open(exportUrl, '_blank')
  }

  return (
    <div className="page-container movementsx-page">
      <div className="page-header">
        <h1>Movimientos</h1>
      </div>

      {/* Filtros */}
      <div className="filters-card">
        <div className="filters-grid">
          <div className="form-group account-selector-group">
            <label>{isUrbana ? 'CVU' : `Cuentas (${selectedAccounts.length} seleccionadas)`}</label>
            {isUrbana ? (
              <input
                type="text"
                value={manualCvu}
                onChange={(e) => setManualCvu(e.target.value)}
                className="form-control"
                placeholder="Ingrese el CVU"
              />
            ) : (
              <div className="account-checkbox-list">
                <label className="account-checkbox-item todas">
                  <input
                    type="checkbox"
                    checked={allAccountsSelected}
                    onChange={toggleAllAccounts}
                  />
                  <span className="checkbox-label">Todas (máx. {MAX_ACCOUNTS} cuentas)</span>
                </label>
                {showLimitMessage && (
                  <div className="account-limit-message">
                    Solo podés seleccionar hasta {MAX_ACCOUNTS} cuentas a la vez
                  </div>
                )}
                <div className="account-checkbox-scroll">
                  {assignedAccounts?.map((acc, index) => (
                    <label key={index} className={`account-checkbox-item ${selectedAccounts.length >= MAX_ACCOUNTS && !selectedAccounts.includes(acc.account_number) ? 'disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedAccounts.includes(acc.account_number)}
                        onChange={() => toggleAccountSelection(acc.account_number)}
                        disabled={selectedAccounts.length >= MAX_ACCOUNTS && !selectedAccounts.includes(acc.account_number)}
                      />
                      <span className="checkbox-label">{acc.account_number}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="movementsx-range">
            <div className="movementsx-range-fields">
              <div className="form-group">
                <label htmlFor="movementsx-from-date">Desde</label>
                <DatePicker
                  id="movementsx-from-date"
                  selected={fromDate}
                  onChange={(date) => setFromDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  maxDate={toDate}
                />
              </div>

              <div className="form-group">
                <label htmlFor="movementsx-to-date">Hasta</label>
                <DatePicker
                  id="movementsx-to-date"
                  selected={toDate}
                  onChange={(date) => setToDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="es"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  minDate={fromDate}
                  maxDate={new Date()}
                />
              </div>

              <div className="form-group buttons-group">
                <label className="movementsx-range-btn-label" aria-hidden="true">&nbsp;</label>
                <div className="buttons-row">
                  <button
                    onClick={handleSearch}
                    className="btn-primary"
                    disabled={loading || (isUrbana ? !manualCvu.trim() : selectedAccounts.length === 0)}
                  >
                    <LuSearch /> {loading ? 'Cargando...' : 'Buscar'}
                  </button>
                  <button
                    type="button"
                    className="btn-export"
                    onClick={() => setShowMoreFilters((open) => !open)}
                  >
                    Filtros
                  </button>
                  <button
                    onClick={handleExport}
                    className="btn-export"
                    disabled={loading || notifications.length === 0}
                    title="Exportar a Excel"
                  >
                    <LuSheet /> Exportar
                  </button>
                </div>
              </div>

              {filterByHour && (
                <>
                  <input
                    type="time"
                    lang="es"
                    step="60"
                    className="form-control movementsx-time"
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                    aria-label="Hora desde"
                  />
                  <input
                    type="time"
                    lang="es"
                    step="60"
                    className="form-control movementsx-time"
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                    aria-label="Hora hasta"
                  />
                  <span className="movementsx-range-time-spacer" aria-hidden="true" />
                </>
              )}
            </div>

            <div className="movementsx-range-switch">
              <label className="hour-filter-toggle">
                <input
                  type="checkbox"
                  checked={filterByHour}
                  onChange={(e) => handleFilterByHourChange(e.target.checked)}
                />
                Filtrar por hora
              </label>
              <p className="movementsx-time-hint">
                {filterByHour
                  ? 'El recorte es exacto: incluye desde y hasta.'
                  : 'Sin hora, el rango toma los días completos.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showMoreFilters && (
      <div className="filters-card filters-secondary">
        <div className="filters-grid-search">
          <div className="form-group">
            <label>Filtrar por Importe</label>
            <input 
              type="text"
              inputMode="decimal"
              value={filterAmount}
              onChange={(e) => setFilterAmount(e.target.value)}
              placeholder="Ej: 1.500, +50.000, -100.000"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Filtrar por CVU</label>
            <input 
              type="text"
              value={filterDescription}
              onChange={(e) => setFilterDescription(e.target.value)}
              placeholder="Buscar por CVU origen o destino..."
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Filtrar por COELSA ID</label>
            <input 
              type="text"
              value={filterCoelsaId}
              onChange={(e) => setFilterCoelsaId(e.target.value)}
              placeholder="Buscar por COELSA ID..."
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Filtrar por Documento</label>
            <input 
              type="text"
              value={filterDocumento}
              onChange={(e) => setFilterDocumento(e.target.value)}
              placeholder="Buscar por CUIT/DNI..."
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>&nbsp;</label>
            <button 
              onClick={handleClearFilters}
              className="btn-clear-filters"
              disabled={!filterAmount && !filterDescription && !filterCoelsaId && !filterDocumento}
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Error */}
      {error && <div className="error-message">{error}</div>}

      {/* Loading */}
      {loading && (
        <div className="loading-movements-full">
          <div className="loading-movements-spinner" />
          <div className="loading-movements-text">
            <span className="loading-movements-title">Cargando movimientos…</span>
            <span className="loading-movements-sub">
              {loadingProgress.total > 1
                ? `Procesando cuenta ${loadingProgress.current} de ${loadingProgress.total}`
                : 'Consultando notificaciones del banco'}
            </span>
          </div>
          <div className="loading-progress-bar">
            <div
              className="loading-progress-fill"
              style={loadingProgress.total > 1 ? {
                width: `${(loadingProgress.current / loadingProgress.total) * 100}%`
              } : undefined}
            />
          </div>
        </div>
      )}

      {/* Sin resultados */}
      {!loading && searchPerformed && movementsWithCommissions.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-large">No se encontraron notificaciones en el período seleccionado</p>
        </div>
      )}

      {/* Tabla de resultados */}
      {!loading && movementsWithCommissions.length > 0 && (() => {
        // Determinar si mostrar columna de cuenta
        const showAccountColumn = !isUrbana && selectedAccounts.length > 1
        
        // Aplicar filtros
        let filteredMovements = movementsWithCommissions
        
        if (filterAmount) {
          const { number: amountFilter, isPositive, isNegative } = cleanFilterAmount(filterAmount)
          
          filteredMovements = filteredMovements.filter(mov => {
            const txData = mov.transaction_data || {}
            const movAmount = parseFloat(txData.amount) || 0
            const absAmount = Math.abs(movAmount)
            const amountMatches = Math.abs(absAmount - amountFilter) < 0.01 || absAmount === amountFilter
            
            if (!amountMatches) return false
            
            const isIncoming = txData.transfer_type === 'E'
            if (isPositive && !isIncoming) return false
            if (isNegative && isIncoming) return false
            
            return true
          })
        }
        
        if (filterDescription) {
          const cvuFilter = filterDescription.trim()
          filteredMovements = filteredMovements.filter(mov => {
            const txData = mov.transaction_data || {}
            const cbuFrom = txData.cbu_from || ''
            const cbuTo = txData.cbu_to || ''
            return (
              cbuFrom.includes(cvuFilter) ||
              cbuTo.includes(cvuFilter)
            )
          })
        }
        
        if (filterCoelsaId) {
          const coelsaFilter = filterCoelsaId.trim().toUpperCase()
          filteredMovements = filteredMovements.filter(mov => {
            const txData = mov.transaction_data || {}
            const coelsaId = (txData.coelsa_id || '').toUpperCase()
            return coelsaId.includes(coelsaFilter)
          })
        }
        
        if (filterDocumento) {
          const docFilter = filterDocumento.trim()
          filteredMovements = filteredMovements.filter(mov => {
            const txData = mov.transaction_data || {}
            const originTaxId = txData.origin_tax_id || ''
            const destTaxId = txData.destination_tax_id || ''
            return originTaxId.includes(docFilter) || destTaxId.includes(docFilter)
          })
        }
        
        if (filteredMovements.length === 0) {
          return (
            <div className="empty-state">
              <p className="empty-state-large">No se encontraron movimientos con los filtros aplicados</p>
            </div>
          )
        }
        
        // Calcular paginación de visualización
        const totalFilteredPages = Math.ceil(filteredMovements.length / PAGE_SIZE)
        const startIndex = (displayPage - 1) * PAGE_SIZE
        const endIndex = startIndex + PAGE_SIZE
        const paginatedMovements = filteredMovements.slice(startIndex, endIndex)
        
        return (
        <>
          <div className="table-container movementsx-table-card" ref={tableRef}>
            <table className="data-table movementsx-data-table">
              <thead>
                <tr>
                  {showAccountColumn && <th>Cuenta</th>}
                  <th>Fecha/Hora</th>
                  <th>Tipo</th>
                  <th>Origen</th>
                  <th>Destino</th>
                  <th>Monto</th>
                  <th>Cód. Autorización</th>
                  <th>COELSA ID</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody key={`page-${displayPage}`}>
                {paginatedMovements.map((mov, index) => {
                  const transferType = getTransferType(mov)
                  const txData = mov.transaction_data || {}
                  const rowKey = `${displayPage}-${startIndex + index}-${mov.id || 'noid'}`

                  return (
                    <tr key={rowKey}>
                      {showAccountColumn && <td className="account-cell">{mov._accountNumber || '-'}</td>}
                      <td>{formatNotificationDate(txData.datetime)}</td>
                      <td className={`transfer-type ${transferType.className}`} title={transferType.label}>
                        {transferType.icon}
                        {mov.webhook_type === 'sendTransfers' && (
                          <span style={{ color: '#ca8a04', fontWeight: 700, fontSize: '0.9rem', marginLeft: '4px' }}>=</span>
                        )}
                      </td>
                      <td>
                        <div className="cell-name">{txData.cardholder_from || '-'}</div>
                        <div className="cell-cvu">{txData.cbu_from !== '-' ? txData.cbu_from : ''}</div>
                        {txData.origin_tax_id && <div className="cell-cvu">{txData.origin_tax_id}</div>}
                      </td>
                      <td>
                        <div className="cell-name">{txData.cardholder_to || '-'}</div>
                        <div className="cell-cvu">{txData.cbu_to !== '-' ? txData.cbu_to : ''}</div>
                        {txData.destination_tax_id && <div className="cell-cvu">{txData.destination_tax_id}</div>}
                      </td>
                      <td className={`amount ${transferType.className}`}>
                        {formatAmountWithSign(mov)}
                      </td>
                      <td>{txData.authorization_code || '-'}</td>
                      <td className="coelsa-id">{txData.coelsa_id || '-'}</td>
                      <td className="text-center">
                        <button 
                          className="btn-download"
                          onClick={() => handleDownloadComprobante(mov)}
                          title="Descargar comprobante"
                        >
                          <LuDownload />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span className="pagination-info-main">
                Mostrando <strong>{startIndex + 1}–{Math.min(endIndex, filteredMovements.length)}</strong> de <strong>{filteredMovements.length.toLocaleString('es-AR')}</strong> movimientos
              </span>
              {(filterAmount || filterDescription || filterCoelsaId || filterDocumento) && (
                <span className="pagination-info-sub">
                  filtrado de {movementsWithCommissions.length.toLocaleString('es-AR')} totales
                </span>
              )}
            </div>

            <div className="pagination-controls">
              <button
                onClick={() => setDisplayPage(1)}
                disabled={displayPage <= 1}
                className="btn-pagination btn-pagination-edge"
                title="Primera página"
                aria-label="Primera página"
              >
                «
              </button>
              <button
                onClick={() => setDisplayPage(displayPage - 1)}
                disabled={displayPage <= 1}
                className="btn-pagination"
              >
                <LuChevronLeft /> Anterior
              </button>

              <span className="pagination-pageinfo">
                Página <strong>{displayPage}</strong> de <strong>{totalFilteredPages}</strong>
              </span>

              <button
                onClick={() => setDisplayPage(displayPage + 1)}
                disabled={displayPage >= totalFilteredPages}
                className="btn-pagination"
              >
                Siguiente <LuChevronRight />
              </button>
              <button
                onClick={() => setDisplayPage(totalFilteredPages)}
                disabled={displayPage >= totalFilteredPages}
                className="btn-pagination btn-pagination-edge"
                title="Última página"
                aria-label="Última página"
              >
                »
              </button>
            </div>
          </div>
        </>
        )
      })()}
    </div>
  )
}

export default MovementsxPage

